import { GAME_HAND, PLAYER_STATUS } from 'constants/gameConstants';
import { useTableSelector } from 'hooks/useTableSelector';
import {
  hasWinnerAnimationStarted,
  hasWinnerDeclarationStarted,
  isMultiRunPreDeal
} from 'utils/functions';

import {
  actionColors,
  computeIsStraddleAllIn,
  isOnlyTheStraddleInFront,
  multiRunAction,
  VALID_ACTIONS
} from 'components/Seat/components/SeatAction';

export const useSeatLogic = (seatId: number) => {
  const game = useTableSelector((table) => table.game);
  const startTurn = useTableSelector((table) => table.gameState.startTurn);
  const straddleSeat = useTableSelector((table) => table.gameState.straddleIndicatorSeat);
  const stack = useTableSelector((table) => table.players?.[seatId]?.stack);
  const betDisplay = useTableSelector((table) => table.players?.[seatId]?.betDisplay);
  const { pots } = game;
  // Which pot is currently being awarded during showdown. The engine pays pots
  // one at a time (main pot, then each side pot) and advances this index; Pot.tsx
  // flies the winning chips for `pots[showdown.currentPot]` off the SAME index.
  const showdown = useTableSelector((table) => (table.game as any).showdown);

  const isHandActive = +game.hand > 0 && +game.hand < 13;
  const isPaused =
    !isHandActive &&
    (!!(game as any).isOnBreak ||
      !!(game as any).isMultiFlightBreak ||
      !!(game as any).isPostBalancing);

  // Only the acting seat during a LIVE hand gets the turn glow. At showdown /
  // hand-end (hand >= 13) the engine leaves game.move + startTurn pointing at
  // the last actor, which otherwise lit that seat with the (gold) turn glow —
  // reading as a spurious highlight next to the real winner. Gate on the hand
  // being active so no one glows once the hand is over.
  const isTurn = isHandActive && seatId.toString() === game.move && startTurn && !isPaused;

  // EN-663 AC-5.3: the vote bubbles stay up while the boards are dealt and clear
  // when a winner is first declared (see `isDismissed` below). This
  // used to blank every seat's action for the whole multi-run sequence
  // (`runs` = "<n>,<hand>,<run>"), which
  // is a multi-run-only treatment of the kind AC-7.2 rules out, and it took the
  // decision record off the felt at the moment players were watching the boards
  // it applied to.
  const rawAction = (game as any)[`p${seatId}action`] || '';

  // The straddle seat is latched in the reducer for the whole pre-flop round
  // (cleared at the flop / showdown / hand-end). Keep showing the 'straddle'
  // indicator for that seat even after the straddler takes their option (e.g.
  // raises pre-flop), which would otherwise overwrite the action and hide it.
  //
  // Two things still show through, so the latch never masks something the seat
  // has just DONE: a fold (a player leaving the hand), and a multi-run vote.
  // The vote is the case that bites pre-flop — a straddler who shoves and gets
  // called opens the window on hand 5, which IS `isPreFlop`, so without this the
  // latch rewrites their answer to 'straddle' and the bubble reads
  // "STRADDLE (ALL IN)" instead of the count they chose. With AC-5.1's status
  // lines gone that bubble is the ONLY record of it (AC-2.4 / AC-3.4).
  const isFolded =
    rawAction.toLowerCase() === 'fold' ||
    String((game as any)[`p${seatId}bet`] || '').charAt(0) === 'F';
  // A multi-run vote is a wire token, not a renderable action — it carries its
  // own copy and palette (AC-5.2). Read from the RAW action for the same reason
  // `isStraddleAllIn` is: the latch below is what we are exempting it from.
  //
  // ...and falling back to the reducer's latched copy, because the raw action
  // only holds the vote for as long as the street it was cast on: the engine
  // blanks `p{N}action` on every deal. That is what took the bubbles off the
  // felt one hand-step after the window closed, instead of at the payout.
  const latchedVote = useTableSelector((table) => table.gameState.multiRunVoteActions?.[seatId]);
  const voteDismissed = useTableSelector((table) => !!table.gameState.multiRunVoteDismissed);
  const multiRun = multiRunAction(rawAction) ?? multiRunAction(latchedVote ?? '');
  // Straddle is a pre-flop-only indicator, but the engine leaves
  // p{N}action='straddle' set after the flop is dealt and even at hand-end
  // (e.g. an all-fold win sets hand=14 without clearing it). Keep it through the
  // ENTIRE pre-flop round and drop it only once the flop is dealt
  // (hand >= DEAL_FLOP = 6) — matching the reducer's straddleIndicatorSeat latch.
  // The previous `< 5` cutoff hid the badge at hand 5, which IS the pre-flop
  // betting round, so it vanished the moment the round began.
  const isPreFlop = +game.hand > 0 && +game.hand < GAME_HAND.DEAL_FLOP;
  const rawIsStraddle = rawAction === 'straddle';
  // The reducer pins `straddleIndicatorSeat` for the WHOLE pre-flop round and
  // only drops it at the flop / showdown / hand-end — it is never cleared by
  // the straddler acting. So the latch alone cannot say whether the straddle
  // label is still current; that is decided by `hasActedOnStraddleOption` below.
  const isLatchedStraddler = straddleSeat === seatId && isPreFlop;

  // An all-in straddle reads as a straddle but should wear the all-in palette
  // (orange/red) and label "STRADDLE (ALL IN)" — matching Unity. CRUCIAL: detect
  // it from the RAW server action, not the latched `playerAction` below. The
  // engine sends action 'allin' for an all-in straddle, but the straddle latch
  // rewrites it to 'straddle' for the pre-flop round — so checking the latched
  // value would always miss it. (The 0-stack fallback in the helper also covers
  // the case where the engine reports 'straddle' with no chips behind.)
  // "STRADDLE (ALL IN)" is a PRE-FLOP-only label. Once the flop is dealt, a
  // straddler who moves all-in is just an all-in player — the straddle context
  // is over — so it must read "ALL IN", not "STRADDLE (ALL IN)". The engine
  // leaves game.hasStraddle flagging the seat (and the 0 stack persists) after
  // the flop, so without this pre-flop gate computeIsStraddleAllIn would keep
  // reporting a straddle all-in through the rest of the hand.
  //
  // ...and only when the straddle WAS the whole stack (EN-11023 feedback): a
  // straddler who goes all-in later in the round has more than the straddle in
  // front, and reads as a plain ALL IN like anyone else.
  const isStraddleAllIn =
    isPreFlop &&
    isOnlyTheStraddleInFront(betDisplay, game.bb) &&
    (computeIsStraddleAllIn(rawAction, game.hasStraddle, seatId, stack) ||
      // The engine clears game.hasStraddle the moment the straddle option is
      // consumed (e.g. an opponent raises over it), which makes the helper above
      // go false. But the straddle seat stays latched for the rest of the
      // pre-flop round, and a latched straddler on a 0 stack is still all-in —
      // so keep the ALL IN qualifier instead of letting it decay to a plain
      // STRADDLE when someone raises.
      ((rawIsStraddle || isLatchedStraddler) && Number(stack) === 0));

  // The straddle label survives only until the straddler acts on their option.
  // Reported: a straddler who had already called still showed a STRADDLE bubble
  // while another seat showed CALL — the latch was rewriting their live action
  // for the rest of the pre-flop round, so the bubble was stale, not early.
  // Once the engine reports a real action for the seat, that action IS the news
  // and must replace the straddle label like it would on any other seat.
  //
  // An all-in straddle is the one action that is NOT a later decision: the
  // engine encodes the straddle itself as 'allin', so it must not be mistaken
  // for the straddler acting — hence the isStraddleAllIn exclusion.
  const hasActedOnStraddleOption =
    VALID_ACTIONS.includes(rawAction) && !rawIsStraddle && !isStraddleAllIn;

  const playerAction =
    isLatchedStraddler && !isFolded && !multiRun && !hasActedOnStraddleOption
      ? 'straddle'
      : rawIsStraddle && !isPreFlop
      ? '' // engine leaves a stale 'straddle' set post-flop / at hand-end
      : rawAction;
  // Mucked hand at showdown: the engine sets the seat's status to one of the
  // MUCK_CARDS variants. Unity surfaces this as a "MUCK" action label (see
  // UIPlayer.OnStatusChanged); we do the same so the mucked player is marked.
  // This takes precedence over any stale betting action still on p{N}action.
  // Gate on the showdown phase (hand >= FIND_WINNERS) like the winner glow
  // below: the engine leaves p{N}status latched at a MUCK_CARDS variant after
  // the hand resets to idle (it only clears on the next status event), so
  // without this gate the "MUCK" label lingered under the seat into the next
  // hand / idle table. The muck decision only happens at showdown, so scoping
  // it there removes the label together with the other hand-end animations.
  const isShowdown = +game.hand >= GAME_HAND.FIND_WINNERS;
  const seatStatus = Number((game as any)[`p${seatId}status`]);
  const isMucked =
    isShowdown &&
    (seatStatus === PLAYER_STATUS.MUCK_CARDS ||
      seatStatus === PLAYER_STATUS.MUCK_CARDS_FOR_SITOUT ||
      seatStatus === PLAYER_STATUS.MUCK_CARDS_FOR_STANDUP);

  const colorAction = isStraddleAllIn ? 'allin' : playerAction;
  // Whether this seat has an action worth showing AT ALL — the input to the
  // hand-end dismissal below, before it is applied.
  //
  // `multiRun` is resolved above, off the raw action, because the straddle latch
  // has to know about it. Checked ahead of VALID_ACTIONS because the vote tokens
  // deliberately are not in that list. It is NOT exempt from the dismissal the
  // way MUCK is: a vote bubble is up while the boards are dealt and gone once a
  // winner is declared.
  const hasActionLabel = isMucked || !!multiRun || VALID_ACTIONS.includes(playerAction);

  // Table-wide rule: the moment the winning animations begin, every seat's
  // last-action speech bubble dismisses. The engine keeps `p{N}action` set
  // through hand-end, so a fold / check / call / bet / raise / all-in bubble
  // used to sit over the felt while the winner pop, the confetti and the
  // pot→seat chips played — including on folded seats, which are exactly the
  // ones whose action never gets overwritten again.
  //
  // Driven off the FIRST beat of the sequence, not the chip flight, so nothing
  // survives into the winner announcement either — see hasWinnerAnimationStarted.
  // Because it is derived here, in the one hook every seat renderer reads, it
  // applies to all nine seats, both orientations and every mounted table
  // (single, carousel panel and grid cell all mount SeatContainer).
  //
  // MUCK is deliberately exempt: it is not a betting action left over from the
  // hand but a showdown-phase state the engine only sets once the hand is being
  // resolved (it mirrors Unity's UIPlayer muck label), so dismissing it here
  // would delete the label outright rather than clean up after it.
  const winnerAnimationStarted = hasWinnerAnimationStarted(game);
  // EN-10899: a multi-run vote goes on the first frame a winner is DECLARED —
  // the signal the winner pop below is gated on — not on the first frame
  // winners reach `pots`. Board 1's winner is in `pots` from the frame boards
  // 2..N start being pre-dealt, so the table-wide signal alone took the vote
  // off the felt as the second board landed, before anyone had won anything.
  //
  // ...and it stays gone for the rest of the hand. Per frame that signal
  // re-opens on later boards, so the reducer records the first declaration
  // (`multiRunVoteDismissed`, see its latch) and this reads it: a later frame
  // with no winners yet cannot bring back a vote token the engine left on the
  // raw row. The live check still covers a hand that never runs out more than
  // one board (a 1X answer), which the reducer does not track.
  const isDismissed = multiRun
    ? voteDismissed || hasWinnerDeclarationStarted(game)
    : winnerAnimationStarted;
  const hasAction = hasActionLabel && (isMucked || !isDismissed);

  // Keyed on the POST-dismissal flag, so the seat card's coloured border/tint
  // clears on the same frame the bubble does. Reported: with the bubble gone
  // but the colour left behind, every non-winner seat still wore its last
  // action (red fold, green call, orange raise) all through the payout, which
  // read as a half-finished dismissal.
  //
  // This does NOT touch the winner's own highlight: both paint sites
  // (SeatCardPill and PortraitCurrentPlayer) select the winner style on
  // `isWinner` and apply the action tint only under `actionColor && !isWinner`,
  // so the winning seat's green card is a separate branch and is unaffected.
  //
  // EN-10898: the vote colour is keyed on the same flag, so it goes with its
  // bubble at the first winner declaration, on every board. It used to be
  // picked ahead of the flag, so the tint outlived the bubble until the reducer
  // latch dropped at the last payout, or for as long as the engine left the
  // vote on the raw action row.
  const actionColor = isMucked
    ? actionColors.muck
    : !hasAction
    ? null
    : multiRun
    ? { bg: multiRun.bg, border: multiRun.border }
    : actionColors[colorAction.toLowerCase()] || null;

  const displayAction = isMucked
    ? 'MUCK'
    : multiRun
    ? multiRun.label
    : isStraddleAllIn
    ? 'STRADDLE (ALL IN)'
    : playerAction.toUpperCase();

  // A seat glows as a winner only if it actually RECEIVES chips from a pot. The
  // engine lists every showdown participant in `pot.winners` (that's how a
  // loser's hand label like "Two Pair / No Low" is surfaced), so membership
  // alone isn't enough — a non-winning shower, or the "no low" side of a Hi-Lo
  // pot, appears with `win: 0`. Gate on `win > 0` so only real pot recipients
  // get the winner highlight.
  //
  // Only the pot CURRENTLY being awarded (`showdown.currentPot`) glows — not
  // every pot at once. The main pot pays first, then each side pot in turn; the
  // glow follows that sequence in lock-step with Pot.tsx's per-pot chip flights,
  // so a main-pot winner and a different side-pot winner never light up together
  // (which confused players about who won what). A split of the SAME pot still
  // lights all its winners simultaneously (they're all in `currentPot.winners`).
  //
  // Hand-history snapshots are static concluded-hand renders. They opt into the
  // same winner presentation without advancing the live table hand phase.
  // In a Hi-Lo game a single pot is split into a Hi half and a Lo half, awarded
  // in two phases (`showdown.handType` flips Hi → Lo). Match the winner's own
  // `handType` against the phase currently paying out — mirroring Pot.tsx's
  // WinChips filter — so a Hi-only winner stops glowing once the Lo half starts
  // paying. Winners in a non-Hi-Lo game carry no `handType`, so `!w.handType`
  // keeps their glow phase-agnostic (unchanged behavior).
  const currentPot = pots?.[Number(showdown?.currentPot) || 0];
  // ...and not while a run-it-twice hand is still dealing its remaining boards.
  // That window sits at FIND_WINNERS with run 1 already evaluated into `pots`,
  // so the pop fired a winner before the last board was on the felt.
  // `hasWinnerDeclarationStarted` is that gate — hand >= FIND_WINNERS with
  // winners landed, outside the pre-deal — shared with the vote dismissal above
  // so the pop and the vote's exit cannot drift apart.
  const isWinner =
    (hasWinnerDeclarationStarted(game) ||
      (Boolean((game as any).handHistorySnapshot) && !isMultiRunPreDeal(game))) &&
    !!currentPot?.winners?.some(
      (w: any) =>
        +w.player === seatId &&
        Number(w.win) > 0 &&
        (!w.handType || w.handType === showdown?.handType)
    );

  return {
    isTurn,
    playerAction,
    // Pre-formatted pill label — "STRADDLE (ALL IN)" for an all-in straddle,
    // otherwise the uppercased action. Renderers should use this for the label.
    displayAction,
    // Whether the seat's action indicator (the speech bubble, or the POV's
    // corner badge in portrait) should be ON SCREEN right now — not merely
    // whether an action exists. It goes false table-wide once the winning
    // animations start.
    hasAction,
    // Null whenever `hasAction` is false, so the seat card's coloured border
    // clears with the bubble rather than outliving it.
    actionColor,
    isWinner,
    isHandActive
  };
};
